import { getAuthHeaders } from "modules/common";
import { createService } from "services/serviceFactory";
import {
    DeleteUserNotificationsRequest,
    ExportNotificationRecipientsRequest,
    GetNotificationTemplatesRequest,
    GetSentNotificationsRequest,
    GetUserNotificaitonsRequest,
    NotificationDeliveryStatisticsRequest,
    NotificationStatisticsFilter,
    SendNotificationRequest,
} from "./request";
import {
    CreateOrUpdateNotificationTemplateView,
    NotificationDeliveryStatisticsResponse,
    NotificationRecipientsStatisticsResponse,
    NotificationStatisticsResponse,
    NotificationTemplate,
    NotificationTemplateAuthorResponse,
    NotificationTemplatesResponse,
    NotificationView,
    SentNotificationsResponse,
    UserNotificationsResponse,
    UserNotificationView,
} from "./response";

const notificationService = createService("account");

// ---------------------
// | SEND NOTIFICATION |
// ---------------------
async function sendNotification(data: SendNotificationRequest, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const formData = new FormData();
    const { attachments, ...notification } = data;

    formData.append("notificationJson", JSON.stringify(notification));
    attachments.forEach(f => formData.append("attachments", f));

    const response = await notificationService.post(
        "/account/v1/notification/sendNotification",
        formData,
        authHeaders
    );
    return response.data;
}

async function getSentNotification(notificationId: string, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.get<NotificationView>(
        `/account/v1/notification/getSentNotification/${notificationId}`,
        authHeaders
    );
    return response;
}

async function getSentNotifications(data: GetSentNotificationsRequest, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<SentNotificationsResponse>(
        "/account/v1/notification/getSentNotifications",
        data,
        authHeaders
    );
    return response;
}

// ---------------------------
// | NOTIFICATION STATISTICS |
// ---------------------------
async function getRecipientsCountByCounty(data: NotificationStatisticsFilter, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationStatisticsResponse>(
        "/account/v1/notification/getRecipientsCountByCounty",
        data,
        authHeaders
    );
    return response;
}

async function getRecipientsCountBySettlement(
    data: NotificationStatisticsFilter,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationStatisticsResponse>(
        "/account/v1/notification/getRecipientsCountBySettlement",
        data,
        authHeaders
    );
    return response;
}

async function getRecipientsCountByRegistry(
    data: NotificationStatisticsFilter,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationStatisticsResponse>(
        "/account/v1/notification/getRecipientsCountByRegistry",
        data,
        authHeaders
    );
    return response;
}

async function getRecipientsCountByIntervention(
    data: NotificationStatisticsFilter,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationStatisticsResponse>(
        "/account/v1/notification/getRecipientsCountByIntervention",
        data,
        authHeaders
    );
    return response;
}

async function getRecipientsStatistics(data: NotificationStatisticsFilter, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationRecipientsStatisticsResponse>(
        "/account/v1/notification/getRecipientsStatistics",
        data,
        authHeaders
    );
    return response;
}

async function getDeliveryStatisticsCounty(
    data: NotificationDeliveryStatisticsRequest,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationDeliveryStatisticsResponse>(
        "/account/v1/notification/getDeliveryStatisticsCounty",
        data,
        authHeaders
    );
    return response;
}

async function getDeliveryStatisticsSettlement(
    data: NotificationDeliveryStatisticsRequest,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationDeliveryStatisticsResponse>(
        "/account/v1/notification/getDeliveryStatisticsSettlement",
        data,
        authHeaders
    );
    return response;
}

// --------------------------
// | NOTIFICATION TEMPLATES |
// --------------------------
async function getNotificationTemplate(notificationTemplate: string, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.get<{ notificationTemplate: NotificationTemplate }>(
        `/account/v1/notification/getNotificationTemplate/${notificationTemplate}`,
        authHeaders
    );
    return response;
}

async function getNotificationTemplates(
    data: GetNotificationTemplatesRequest,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<NotificationTemplatesResponse>(
        "/account/v1/notification/getNotificationTemplates",
        data,
        authHeaders
    );
    return response;
}

async function createOrUpdateNotificationTemplate(
    data: CreateOrUpdateNotificationTemplateView,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post(
        "/account/v1/notification/createOrUpdateNotificationTemplate",
        { notificationTemplate: data },
        authHeaders
    );
    return response;
}

async function deleteNotificationTemplate(notificationTemplateId: string, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.delete(
        `/account/v1/notification/deleteNotificationTemplate/${notificationTemplateId}`,
        authHeaders
    );
    return response;
}

async function getNotificationTemplateAuthors(accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.get<{
        notificationTemplateAuthors: NotificationTemplateAuthorResponse[];
    }>("/account/v1/notification/getNotificationTemplateAuthors", authHeaders);
    return response;
}

async function exportNotificationRecipients(
    data: ExportNotificationRecipientsRequest,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post(
        "/account/v1/notification/exportNotificationRecipients",
        data,
        {
            responseType: "blob",
            ...authHeaders,
        }
    );
    return response.data;
}

// ---------------------
// | USER NOTIFICATION |
// ---------------------
async function getUserNotification(userNotificationId: string, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.get<{ userNotification: UserNotificationView }>(
        `/account/v1/notification/getUserNotification/${userNotificationId}`,
        authHeaders
    );
    return response;
}

async function getUserNotifications(data: GetUserNotificaitonsRequest, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post<UserNotificationsResponse>(
        "/account/v1/notification/getUserNotifications",
        data,
        authHeaders
    );
    return response;
}

async function markNotificationRead(userNotificationId: string, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.get(
        `/account/v1/notification/markNotificationRead/${userNotificationId}`,
        authHeaders
    );
    return response;
}

async function markNotificationsRead(userNotificationIds: string[], accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post(
        "/account/v1/notification/markNotificationRead",
        { userNotificationIds: userNotificationIds },
        authHeaders
    );
    return response;
}

async function highlightUserNotification(userNotificationId: string, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.get(
        `/account/v1/notification/highlightUserNotification/${userNotificationId}`,
        authHeaders
    );
    return response;
}

async function deleteUserNotifications(
    userNotificationIds: DeleteUserNotificationsRequest,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    const response = await notificationService.post(
        "/account/v1/notification/deleteUserNotifications",
        userNotificationIds,
        authHeaders
    );
    return response;
}

export {
    createOrUpdateNotificationTemplate,
    deleteNotificationTemplate,
    deleteUserNotifications,
    exportNotificationRecipients,
    getDeliveryStatisticsCounty,
    getDeliveryStatisticsSettlement,
    getNotificationTemplate,
    getNotificationTemplateAuthors,
    getNotificationTemplates,
    getRecipientsCountByCounty,
    getRecipientsCountByIntervention,
    getRecipientsCountByRegistry,
    getRecipientsCountBySettlement,
    getRecipientsStatistics,
    getSentNotification,
    getSentNotifications,
    getUserNotification,
    getUserNotifications,
    highlightUserNotification,
    markNotificationRead,
    markNotificationsRead,
    sendNotification,
};
